import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button, ConfigProvider, Table, TableProps, Tag, Tooltip, Typography, theme as antdTheme } from "antd";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { Input } from "@/components/ui/inputForm";
import { MatriculaAlunoSchema } from "@/schema/matriculaAlunoSchema";
import { getAllProdutos } from "@/services/produto";
import { errorHandler } from "@/utils/errorhandler";
import { getUnidadeLogadaId } from "@/utils/unidadeLogada";
import { somarProdutos, valoresDoProduto } from "@/utils/totaisMatricula";
import ProdutoModal, { valorNaUnidade, valorVinculadoNaUnidade } from "@/components/produtoModal";

const { Title } = Typography;

type ProdutosProps = {
  current?: number;
  disabled?: boolean;
};

function formatCurrency(valor: number) {
  return (valor || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" });
}

function parseCurrency(value: string) {
  if (!value) return 0;
  return Number(value.replace(/[^\d]/g, "")) / 100;
}

export default function Produtos({ disabled }: ProdutosProps) {
  const { watch, setValue } = useFormContext<MatriculaAlunoSchema>();
  const [catalogo, setCatalogo] = useState<any[]>([]);
  const [modalAberto, setModalAberto] = useState(false);
  const unidadeId = useMemo(() => getUnidadeLogadaId(), []);

  const produtos = watch("produtos") || [];
  const disciplinas = watch("disciplinas") || [];

  useEffect(() => {
    const carregar = async () => {
      try {
        const response = await getAllProdutos();
        if (response.status === 200) {
          setCatalogo((response.data || []).filter((p: any) => p.ativo));
        }
      } catch (error) {
        errorHandler(error);
      }
    };
    carregar();
  }, []);

  /**
   * Ids das específicas selecionadas no step anterior. A lista `disciplinas` do
   * formulário guarda a específica no `codEspecificaServico` ("E" + id) — o
   * `disciplinaId` nem sempre é gravado na seleção, por isso o id sai do código.
   */
  const especificasSelecionadas = useMemo(
    () =>
      disciplinas
        .map((d: any) => {
          const codigo = String(d.codEspecificaServico ?? "");
          if (codigo.startsWith("E")) {
            const id = Number(codigo.slice(1));
            return Number.isFinite(id) ? id : null;
          }
          return null;
        })
        .filter((id: number | null): id is number => id !== null),
    [disciplinas]
  );

  /**
   * Produto vinculado a uma específica entra e sai da lista junto com ela; o que
   * foi adicionado à mão (sem especificaOrigemId) nunca é mexido aqui.
   */
  useEffect(() => {
    if (catalogo.length === 0) return;

    const automaticosEsperados = catalogo
      .map((produto: any) => {
        const especifica = (produto.especificas || []).find((e: any) =>
          especificasSelecionadas.includes(e.especificaId)
        );
        return especifica ? { produto, especificaOrigemId: especifica.especificaId } : null;
      })
      .filter(Boolean) as { produto: any; especificaOrigemId: number }[];

    const manuais = produtos.filter((p: any) => !p.especificaOrigemId);
    // O produto que vem por vínculo é cobrado pelo "Valor Vinculado" do cadastro
    // do produto (não pelo valor avulso). O desconto informado na linha é
    // preservado; o preço em si é sempre o do catálogo.
    const automaticosMantidos = produtos
      .filter(
        (p: any) =>
          p.especificaOrigemId &&
          automaticosEsperados.some((a) => a.produto.id === p.produtoId)
      )
      .map((p: any) => {
        const esperado = automaticosEsperados.find((a) => a.produto.id === p.produtoId)!;
        const valor = valorVinculadoNaUnidade(esperado.produto, unidadeId);
        return { ...p, valor, ...valoresDoProduto(valor, p.percDesconto ?? 0) };
      });
    const novos = automaticosEsperados
      .filter(
        (a) =>
          !manuais.some((p: any) => p.produtoId === a.produto.id) &&
          !automaticosMantidos.some((p: any) => p.produtoId === a.produto.id)
      )
      .map((a) => {
        const valor = valorVinculadoNaUnidade(a.produto, unidadeId);
        return {
          produtoId: a.produto.id,
          nome: a.produto.nome,
          valor,
          ...valoresDoProduto(valor, 0),
          especificaOrigemId: a.especificaOrigemId,
        };
      });

    const proximo = [...manuais, ...automaticosMantidos, ...novos];
    const chave = (lista: any[]) =>
      lista
        .map(
          (p) =>
            `${p.produtoId}:${p.especificaOrigemId ?? ""}:${p.valor ?? ""}:${p.percDesconto ?? ""}:${p.valorFinal ?? ""}`
        )
        .join("|");

    if (chave(proximo) !== chave(produtos)) {
      setValue("produtos", proximo as any, { shouldValidate: false });
    }
  }, [catalogo, especificasSelecionadas, produtos, setValue, unidadeId]);

  /** Entram todos os produtos confirmados na modal de uma vez. */
  const adicionarProdutos = useCallback(
    (escolhidos: any[]) => {
      const novos = escolhidos.map((produto) => {
        const valor = valorNaUnidade(produto, unidadeId);
        return {
          produtoId: produto.id,
          nome: produto.nome,
          valor,
          ...valoresDoProduto(valor, 0),
        };
      });
      setValue("produtos", [...produtos, ...novos] as any, { shouldValidate: false });
      setModalAberto(false);
    },
    [produtos, setValue, unidadeId]
  );

  const removerProduto = (index: number) => {
    setValue(
      "produtos",
      produtos.filter((_: any, i: number) => i !== index) as any,
      { shouldValidate: false }
    );
  };

  /** Recalcula desconto e valor final da linha a partir do valor e do percentual. */
  const atualizarLinha = (index: number, valor: number, percDesconto: number) => {
    setValue(
      "produtos",
      produtos.map((produto: any, i: number) =>
        i === index ? { ...produto, valor, ...valoresDoProduto(valor, percDesconto) } : produto
      ) as any,
      { shouldValidate: false }
    );
  };

  const nomeDaEspecifica = (especificaId?: number | null) =>
    disciplinas.find((d: any) => d.codEspecificaServico === `E${especificaId}`)?.nome;

  const columns: TableProps<any>["columns"] = [
    {
      title: "Produto",
      dataIndex: "nome",
      key: "nome",
    },
    {
      title: "Origem",
      key: "origem",
      render: (_, record) =>
        record.especificaOrigemId ? (
          <Tag color="blue">{`Específica: ${nomeDaEspecifica(record.especificaOrigemId) ?? "vinculada"}`}</Tag>
        ) : (
          <Tag>Manual</Tag>
        ),
    },
    {
      title: "Valor",
      key: "valor",
      width: 160,
      render: (_, record, index) => (
        <Input
          type="text"
          aria-label={`Valor - ${record.nome}`}
          // Produto vinculado tem preço definido no cadastro ("Valor Vinculado").
          disabled={disabled || Boolean(record.especificaOrigemId)}
          value={formatCurrency(record.valor)}
          onChange={(e) =>
            atualizarLinha(index, parseCurrency(e.target.value), record.percDesconto ?? 0)
          }
        />
      ),
    },
    {
      title: "% Desconto",
      key: "percDesconto",
      width: 120,
      render: (_, record, index) => (
        <Input
          type="number"
          aria-label={`% Desconto - ${record.nome}`}
          disabled={disabled}
          value={String(record.percDesconto ?? 0)}
          onChange={(e) => atualizarLinha(index, record.valor ?? 0, Number(e.target.value))}
        />
      ),
    },
    {
      title: "Valor Final",
      key: "valorFinal",
      width: 140,
      render: (_, record) => formatCurrency(valoresDoProduto(record.valor, record.percDesconto ?? 0).valorFinal),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, record, index) =>
        record.especificaOrigemId ? (
          <Tooltip title="Produto vinculado à específica: desmarque a específica para removê-lo">
            <Button type="text" danger icon={<DeleteOutlined />} disabled />
          </Tooltip>
        ) : (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            title="Remover produto"
            disabled={disabled}
            onClick={() => removerProduto(index)}
          />
        ),
    },
  ];

  return (
    <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
      <div className="mb-4 flex items-center justify-between">
        <Title level={4} style={{ margin: 0 }}>Produtos</Title>
        <Button
          type="primary"
          icon={<SearchOutlined />}
          disabled={disabled}
          onClick={() => setModalAberto(true)}
        >
          Pesquisar produto
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={produtos.map((p: any, index: number) => ({ ...p, key: `${p.produtoId}-${index}` }))}
        rowKey="key"
        size="small"
        pagination={false}
        locale={{ emptyText: "Nenhum produto adicionado" }}
      />

      <div className="mt-4">
        <Title level={5}>{`Total em produtos: ${formatCurrency(somarProdutos(produtos))}`}</Title>
      </div>

      <ProdutoModal
        open={modalAberto}
        produtos={catalogo}
        idsAdicionados={produtos.map((p: any) => p.produtoId)}
        unidadeId={unidadeId}
        onConfirm={adicionarProdutos}
        onCancel={() => setModalAberto(false)}
      />
    </ConfigProvider>
  );
}
